<template>
	<div>
		<div class="text-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<b-table v-else-if="detectors?.length > 0" small :items="detectors" :fields="['word','message','flags','media',{ label: '', key: 'actions', thClass: 'text-right', tdClass: 'text-right'}]">
			<template #cell(word)="data">
				<b-form-checkbox switch v-model="data.item.status" value="enabled" unchecked-value="disabled" @input="setItemStatus(data.item.id, data.item.status)" :disabled="data.item.changingStatus">{{data.item.word}}</b-form-checkbox>
			</template>
			<template #cell(media)="data">
				<span v-if="data.value && data.value.length">
					{{data.value[0].filename||data.value[0].md5}} ({{bytesString(data.value[0].size)}}B)
					<span class="text-muted" v-if="data.value.length > 1"> and {{data.value.length - 1}} more</span>
				</span>
			</template>
			<template #cell(flags)="data">
				{{Object.keys(data.value).join(', ')}}
			</template>
			<template #cell(actions)="data">
				<b-button-group size="sm">
					<b-button variant="success" @click="edit(data.item.id)"><b-icon icon="pencil-fill"/></b-button>
					<b-button variant="danger" @click="$bvModal.msgBoxConfirm('Remove this word?', { title: 'Please Confirm', size: 'sm', buttonSize: 'sm', okVariant: 'danger', footerClass: 'p-2', hideHeaderClose: false, centered: true }).then(value => value&&removeItem(data.item.id))"><b-icon icon="trash"></b-icon></b-button>
				</b-button-group>
			</template>
		</b-table>
		<b-card v-else class="text-center text-muted p-5">No tower detectors.<br />Tower detectors are a fun way to interact with chat. Have chatters spell out a word, one message at a time and trigger a notification when they succeed!<br /></b-card>
		<div class="text-center my-3">
			<b-button @click="edit()" variant="success"><b-icon icon="plus"></b-icon> Add a word</b-button>
		</div>
		<b-modal ref="editModal" title="Edit word detector">
			<b-form @submit.stop.prevent="save(); return false" id="towerEditForm">
				<b-form-row>
					<b-col>
						<b-form-group label="Word">
							<b-form-input v-model="curDetector.word" required pattern="[A-Za-z0-9]+"/>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message to display">
							<b-form-input v-model="curDetector.message"/>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-row>
					<b-col>
						<b-form-group>
							<b-form-checkbox switch v-model="curDetector.flags['cross-platform']">Cross platform</b-form-checkbox>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group>
							<b-form-checkbox switch v-model="curDetector.flags['same-chatter']">Allow same chatter</b-form-checkbox>
						</b-form-group>
					</b-col>
				</b-form-row>
				<p class="text-muted">Users can use case insensitive and special characters (𝔸𝐁ℭ🅳)</p>
				<b-form-group label="Media" description="Sounds, images and video">
					<pipi-pupu-multi-file v-model="curDetector.media" accept="image/*,audio/*,video/*" />
				</b-form-group>
			</b-form>
			<template #modal-footer="{cancel}">
				<div class="w-100">
					<b-alert variant="danger" :show="!!saveError">{{saveError}}</b-alert>
					<div class="float-left" v-if="curDetector.id">
						<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
					</div>
					<div class="float-right">
						<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
						<b-button variant="success" type="submit" form="towerEditForm" class="mr-2">
							Save
						</b-button>
						<b-button @click="cancel()">
							Cancel
						</b-button>
					</div>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import PipiPupuMultiFile from '@/components/PipiPupuMultiFile.vue';
import util from '@/util.js';

export default {
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		detectors: [],
		curDetector: { flags: {} },
	}},
	components: {
		PipiPupuMultiFile,
	},
	async mounted() {
		await this.loadDetectors();
	},
	methods: {
		async loadDetectors() {
			try {
				this.loading++;
				this.error = null;
				this.detectors = await this.$api.get('towers');
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async edit(id) {
			console.log('edit', id);
			try {
				this.saving++;
				this.saveError = null;
				this.$refs.editModal.show();
				if(id) {
					this.curDetector = { flags: {} };
					this.curDetector = await this.$api.get(`tower/${encodeURIComponent(id)}`);
				} else {
					this.curDetector = {
						word: '',
						string: '',
						flags: {
							'cross-platform': false,
							'same-chatter': false,
							'case-sensitive': false,
							'special-characters': false,
						},
						media: [],
					};
				}
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		async removeItem(id) {
			await this.$api.delete(`tower/${encodeURIComponent(id)}`);
			await this.loadDetectors();
		},
		async setItemStatus(id, status) {
			await this.$api.patch(`tower/${encodeURIComponent(id)}/status`, status);
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;
				if(this.curDetector.id)
					await this.$api.patch(`tower/${encodeURIComponent(this.curDetector.id)}`, this.curDetector);
				else
					await this.$api.post('tower', this.curDetector);
				this.$refs.editModal.hide();
				await this.loadDetectors();
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		bytesString(n, p) { return util.bytesString(n, p); },
	},
};
</script>
